import { ArrowUpRight, Check } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { useSession } from "../auth/useSession";
import { useCampaigns } from "../campaigns/useCampaigns";
import { useProducts } from "../products/useProducts";
import { useThemeEmbedAck } from "./useThemeEmbedAck";

interface Step {
	title: string;
	instruction: string;
	done: boolean;
	action: ReactNode;
}

const StepRow = ({ step, index, active }: { step: Step; index: number; active: boolean }) => (
	<li className="flex items-start gap-3 py-3">
		<span
			className={
				step.done
					? "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink text-surface"
					: "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-line-strong text-[11px] font-medium text-ink-muted"
			}
		>
			{step.done ? <Check className="h-3 w-3" strokeWidth={3} /> : index + 1}
		</span>

		<div className="min-w-0 flex-1">
			<p className={step.done ? "text-sm text-ink-muted line-through" : "text-sm text-ink"}>
				{step.title}
			</p>
			{step.done ? null : <p className="mt-0.5 text-xs text-ink-muted">{step.instruction}</p>}
		</div>

		{step.done || !active ? null : <div className="shrink-0">{step.action}</div>}
	</li>
);

/**
 * Shown until every step is done, and after that only when asked for by
 * `?setup=1`. There is no dismiss: a checklist a merchant can hide while their
 * app is switched off in the theme is a support ticket waiting to happen.
 */
export const OnboardingChecklist = () => {
	const [params] = useSearchParams();
	const asked = params.get("setup") === "1";
	const session = useSession();
	const campaigns = useCampaigns();
	const products = useProducts({ search: "", status: "", limit: 1, offset: 0 });
	const shopDomain = session.data?.tenant.shopDomain;
	const { acknowledged, acknowledge } = useThemeEmbedAck(shopDomain);

	if (campaigns.isPending || products.isPending) return null;

	const items = campaigns.data?.items ?? [];

	const steps: Step[] = [
		{
			title: "Turn the app on in your theme",
			instruction:
				"Open your theme editor, go to App embeds and enable AOVtools Content. Nothing shows on the storefront until this is on.",
			done: acknowledged,
			action: (
				<div className="flex items-center gap-2">
					<a
						href={`https://${shopDomain}/admin/themes/current/editor?context=apps`}
						target="_blank"
						rel="noreferrer"
					>
						<Button variant="outline" size="sm">
							Open theme editor
							<ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
						</Button>
					</a>
					<Button size="sm" onClick={acknowledge}>
						Done
					</Button>
				</div>
			),
		},
		{
			title: "Wait for your products to sync",
			instruction:
				"We copy your catalog when you install so offers can point at real products. It usually takes under a minute.",
			done: (products.data?.total ?? 0) > 0,
			action: (
				<Link to="/products">
					<Button variant="outline" size="sm">
						Check sync
					</Button>
				</Link>
			),
		},
		{
			title: "Create your first offer",
			instruction:
				"Start with a simple free gift. Give it a name, pick the product a shopper receives, and set the spend they have to reach.",
			done: items.length > 0,
			action: (
				<Link to="/campaigns">
					<Button size="sm">Create an offer</Button>
				</Link>
			),
		},
		{
			title: "Publish it",
			instruction:
				"Set the offer to Active, then press Publish. Edits stay in draft until you publish again.",
			done: items.some((campaign) => campaign.publishedAt !== null),
			action: (
				<Link to="/campaigns">
					<Button size="sm">Go to offers</Button>
				</Link>
			),
		},
	];

	const complete = steps.filter((step) => step.done).length;
	const finished = complete === steps.length;
	if (finished && !asked) return null;

	const current = steps.findIndex((step) => !step.done);

	return (
		<Card className="p-0">
			<div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-4">
				<div>
					<h3 className="text-sm font-medium text-ink">
						{finished ? "Setup guide" : "Finish setting up"}
					</h3>
					<p className="mt-0.5 text-xs text-ink-muted">
						{finished
							? "Everything is done. Kept here so you can retrace the steps."
							: "Four steps to your first offer running on the storefront."}
					</p>
				</div>
				<span className="text-xs text-ink-muted">
					{complete} of {steps.length} done
				</span>
			</div>

			<ul className="divide-y divide-line px-5">
				{steps.map((step, index) => (
					<StepRow key={step.title} step={step} index={index} active={index === current} />
				))}
			</ul>
		</Card>
	);
};
